import config from '../config.json';
import { isAvatarMenuMobile } from '../utils/avatarMenuMode.js';
import { getTrackingSection } from '../utils/trackingSection.js';
import { USER_TYPE } from '../utils/types.js';

// The "Browse spaces" menu. Its markup comes from shared/navigation/browse-spaces.twig, so the links
// of the page's own city are in the HTML; from 992px this module drives that markup as a dropdown,
// below it hands the same links to the layered avatar menu. Any other city is fetched on click.

const ROOT_SELECTOR = '.js-browse-spaces';
const CITY_LINKS_URL = '/browse-spaces/cities/';
const READY_CLASS = 'ds-browse-spaces--ready';
// Keeps the avatar menu in its mobile form on a tablet, where the menu is one of its rows.
const HEADER_CLASS = 'ds-header--browse-spaces';
const OPEN_CLASS = 'ds-browse-spaces--open';
const LOADING_CLASS = 'ds-browse-spaces__city--loading';
const CURRENT_LINK_CLASS = 'ds-browse-spaces__link--current';
const HEADER_CONTENT_SELECTOR = '.ds-header__content';
// The header's logo and links sit this far inside its content box.
const HEADER_INSET = 16;

const LINK_NAME = 'browseSpaces';
const LABEL = 'Browse spaces';
const TRACKING_ELEMENT = 'browse_spaces';

const GROUPS = [
  { name: 'eventTypes', title: 'Event types' },
  { name: 'venueTypes', title: 'Venue types' },
];

// The navbar rebuilds itself on every breakpoint crossing and login change, so everything below is
// set up once per page and reads the viewer from here rather than from the call that set it up.
let viewer = null;
let menuData = null;
let isDropdownBound = false;
let isVisibleTracked = false;
const cityLinksRequests = new Map();

function findRoot() {
  return document.querySelector(ROOT_SELECTOR);
}

// Logged-out visitors only: the menu is there to lead them, and crawlers, through the indexed pages. A
// Client or a venue manager is past that point, and the cached page cannot leave the menu out for them.
function isAvailableFor(user) {
  return user.type === USER_TYPE.GUEST;
}

function trackInteraction(interaction, elementValue = null) {
  App.track.event('navbar_interaction', {
    section: getTrackingSection(viewer?.type),
    element: TRACKING_ELEMENT,
    ...(elementValue ? { element_value: elementValue } : {}),
    interaction,
  });
}

function readLinks(container) {
  return [...container.querySelectorAll('a')].map(link => ({
    label: link.textContent.trim(),
    url: link.getAttribute('href'),
  }));
}

function readCity(step) {
  const city = {
    locationId: Number(step.dataset.locationId),
    name: step.querySelector('.js-browse-spaces-city-name').textContent.trim(),
  };

  GROUPS.forEach(({ name }) => {
    const group = step.querySelector(`.js-browse-spaces-group[data-group="${name}"]`);
    city[name] = group ? readLinks(group) : [];
  });

  return city;
}

function readCitySection(section) {
  return {
    title: section.querySelector('.js-browse-spaces-city-section-title').textContent.trim(),
    cities: [...section.querySelectorAll('.js-browse-spaces-city')].map(link => ({
      locationId: Number(link.dataset.locationId),
      name: link.querySelector('.js-browse-spaces-city-label').textContent.trim(),
      url: link.getAttribute('href'),
      countryCode: link.querySelector('.js-browse-spaces-city-country')?.textContent.trim() ?? null,
    })),
  };
}

// Read before anything is added to the markup: a city fetched later is appended as a step of its
// own, and must not be taken for the page's city.
function readMenu(root) {
  const currentStep = root.querySelector('.js-browse-spaces-city-step');
  const citySections = [...root.querySelectorAll('.js-browse-spaces-city-section')].map(readCitySection);

  return {
    citySections,
    cities: citySections.flatMap(section => section.cities),
    currentCity: currentStep ? readCity(currentStep) : null,
    showAllCities: citySections.length > 0,
  };
}

function getMenu(root) {
  if (!menuData) {
    menuData = readMenu(root);

    if (menuData.currentCity) {
      cityLinksRequests.set(menuData.currentCity.locationId, Promise.resolve(menuData.currentCity));
    }
  }

  return menuData;
}

function fetchCityLinks(locationId) {
  if (!cityLinksRequests.has(locationId)) {
    const request = fetch(`${CITY_LINKS_URL}${locationId}`, { headers: { Accept: 'application/json' } })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Browse spaces: no links for city ${locationId} (HTTP ${response.status})`);
        }

        return response.json();
      });

    // A failed request is not remembered, so the next click tries again.
    request.catch(() => cityLinksRequests.delete(locationId));
    cityLinksRequests.set(locationId, request);
  }

  return cityLinksRequests.get(locationId);
}

const withoutTrailingSlash = path => path.replace(/\/+$/, '');

function isCurrentPage(url) {
  const { pathname } = new URL(url, window.location.origin);

  return withoutTrailingSlash(pathname) === withoutTrailingSlash(window.location.pathname);
}

// A link to the page in view stays a link, but is marked as where the viewer is.
function markIfCurrentPage(link) {
  if (isCurrentPage(link.getAttribute('href'))) {
    link.classList.add(CURRENT_LINK_CLASS);
    link.setAttribute('aria-current', 'page');
  }
}

function createElement(tagName, className, text = null) {
  const element = document.createElement(tagName);
  element.className = className;

  if (text !== null) {
    element.appendChild(document.createTextNode(text));
  }

  return element;
}

// Mirrors shared/navigation/browse-spaces-city.twig.
function createCityStep(city, { showAllCities }) {
  const step = createElement('div', 'ds-browse-spaces__step js-browse-spaces-city-step');
  step.dataset.locationId = city.locationId;

  if (showAllCities) {
    const back = createElement('button', 'ds-browse-spaces__back js-browse-spaces-back', 'All cities');
    back.type = 'button';
    step.appendChild(back);
  }

  step.appendChild(createElement('p', 'ds-browse-spaces__city-name js-browse-spaces-city-name', city.name));

  const groups = createElement('div', 'ds-browse-spaces__groups');

  GROUPS.filter(({ name }) => city[name]?.length).forEach(({ name, title }) => {
    const group = createElement('div', 'ds-browse-spaces__group js-browse-spaces-group');
    group.dataset.group = name;
    group.appendChild(createElement('p', 'ds-browse-spaces__title js-browse-spaces-group-title', title));

    const list = document.createElement('ul');
    city[name].forEach(({ label, url }) => {
      const item = document.createElement('li');
      const link = createElement('a', 'ds-browse-spaces__link', label);
      link.href = url;
      markIfCurrentPage(link);
      item.appendChild(link);
      list.appendChild(item);
    });

    group.appendChild(list);
    groups.appendChild(group);
  });

  step.appendChild(groups);

  return step;
}

function bindDropdown(root) {
  const toggle = root.querySelector('.js-browse-spaces-toggle');
  const panel = root.querySelector('.js-browse-spaces-panel');
  const citiesStep = root.querySelector('.js-browse-spaces-cities');
  // Opening lands on the page's own city when it has one, as the design has it; "All cities" is one
  // click back from there.
  const initialStep = root.querySelector('.js-browse-spaces-city-step') ?? citiesStep;

  panel.querySelectorAll('.js-browse-spaces-group a').forEach(markIfCurrentPage);

  const isOpen = () => root.classList.contains(OPEN_CLASS);

  // The panel's right edge lines up with the last navbar link ("List your space"), so it never runs
  // into the page margin.
  function positionPanel() {
    const { left: rootLeft } = root.getBoundingClientRect();
    const { right: contentRight } = root.closest(HEADER_CONTENT_SELECTOR).getBoundingClientRect();

    panel.style.left = `${contentRight - HEADER_INSET - panel.offsetWidth - rootLeft}px`;
  }

  // The list of cities is wider than a city's links, so the panel is placed again for every step.
  function showStep(step) {
    panel.querySelectorAll('.ds-browse-spaces__step').forEach(candidate => {
      candidate.hidden = candidate !== step;
    });

    if (isOpen()) {
      positionPanel();
    }
  }

  function open() {
    root.classList.add(OPEN_CLASS);
    toggle.setAttribute('aria-expanded', 'true');
    showStep(initialStep);
  }

  function close() {
    root.classList.remove(OPEN_CLASS);
    toggle.setAttribute('aria-expanded', 'false');
  }

  async function openCity(cityLink) {
    const locationId = Number(cityLink.dataset.locationId);
    const existingStep = panel.querySelector(`.js-browse-spaces-city-step[data-location-id="${locationId}"]`);

    if (existingStep) {
      showStep(existingStep);
      return;
    }

    cityLink.classList.add(LOADING_CLASS);

    try {
      const step = createCityStep(await fetchCityLinks(locationId), { showAllCities: true });
      panel.appendChild(step);
      showStep(step);
    } catch {
      // The city page is the next best place to browse that city from.
      window.location.href = cityLink.href;
    } finally {
      cityLink.classList.remove(LOADING_CLASS);
    }
  }

  toggle.addEventListener('click', () => {
    if (isOpen()) {
      close();
      return;
    }

    trackInteraction('click');
    open();
  });

  panel.addEventListener('click', event => {
    if (event.target.closest('.js-browse-spaces-back')) {
      showStep(citiesStep);
      return;
    }

    const cityLink = event.target.closest('.js-browse-spaces-city');
    if (cityLink) {
      event.preventDefault();
      openCity(cityLink);
      return;
    }

    const link = event.target.closest('a');
    if (link) {
      trackInteraction('click', link.getAttribute('href'));
    }
  });

  document.addEventListener('click', event => {
    if (isOpen() && !root.contains(event.target)) {
      close();
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && isOpen()) {
      close();
      toggle.focus();
    }
  });
}

/**
 * Reveals the desktop menu for the viewer and reports it as seen. Called on every navbar render,
 * before the navbar is laid out, which depends on whether the viewer is offered the menu.
 */
export function initBrowseSpaces(user) {
  const root = findRoot();

  if (!root) {
    return;
  }

  viewer = user;
  getMenu(root);

  const isAvailable = isAvailableFor(user);
  root.classList.toggle(READY_CLASS, isAvailable);
  root.closest('.ds-header')?.classList.toggle(HEADER_CLASS, isAvailable);

  if (!isAvailable) {
    root.classList.remove(OPEN_CLASS);
    return;
  }

  if (!isDropdownBound) {
    isDropdownBound = true;
    bindDropdown(root);
  }

  if (!isVisibleTracked) {
    isVisibleTracked = true;
    trackInteraction('visible');
  }
}

const cityPanelName = locationId => `${LINK_NAME}City${locationId}`;

// Only the page's own city can link to the page in view; every other city's links lead elsewhere.
function isInMenu(menu) {
  return GROUPS.some(({ name }) => menu.currentCity?.[name].some(({ url }) => isCurrentPage(url)));
}

function createCityRows(city) {
  return GROUPS.filter(({ name }) => city[name]?.length).flatMap(({ name, title }) => [
    { name: config.sectionTitleName, label: title },
    ...city[name].map(({ label, url }) => ({
      name: `${LINK_NAME}Link`,
      label,
      url,
      options: {
        chevron: true,
        current: isCurrentPage(url),
        tracking: {
          section: getTrackingSection(viewer?.type),
          element: TRACKING_ELEMENT,
          elementValue: url,
        },
      },
    })),
  ]);
}

function addCityPanel({ addPanel, city, parentName }) {
  addPanel({
    name: cityPanelName(city.locationId),
    label: city.name,
    links: createCityRows(city),
    parentName,
  });
}

// Opening lands on the page's own city when it has one, with the list of cities one level back - the
// same "All cities" step the desktop dropdown has.
function rootPanelName(menu) {
  return menu.currentCity ? cityPanelName(menu.currentCity.locationId) : LINK_NAME;
}

// A city row whose panel does not exist yet is left to navigate on its own by menuPanels, so this
// takes over that click, fetches the city and adds its panel. Once the panel exists menuPanels
// opens it and this handler stands aside.
function bindCityRows({ citiesPanel, menu, addPanel, showPanel, hasPanel }) {
  const pendingPanels = new Set();

  citiesPanel.querySelectorAll('[data-panel-target]').forEach(row => {
    const panelName = row.dataset.panelTarget;
    const city = menu.cities.find(candidate => cityPanelName(candidate.locationId) === panelName);

    row.addEventListener('click', async event => {
      if (hasPanel(panelName)) {
        return;
      }

      event.preventDefault();

      if (pendingPanels.has(panelName)) {
        return;
      }

      pendingPanels.add(panelName);

      try {
        addCityPanel({ addPanel, city: await fetchCityLinks(city.locationId), parentName: LINK_NAME });
        showPanel(panelName);
      } catch {
        window.location.href = city.url;
      } finally {
        pendingPanels.delete(panelName);
      }
    });
  });
}

// The same sections as the desktop list of cities, each under its title.
function createCitiesRows(menu) {
  return menu.citySections.flatMap(({ title, cities }) => [
    { name: config.sectionTitleName, label: title },
    ...cities.map(city => ({
      name: `${LINK_NAME}City`,
      label: city.name,
      url: city.url,
      options: { panelTarget: cityPanelName(city.locationId), suffix: city.countryCode },
    })),
  ]);
}

function createPanels(menu) {
  return ({ element, addPanel, showPanel, hasPanel }) => {
    if (menu.showAllCities) {
      const citiesPanel = addPanel({
        name: LINK_NAME,
        label: LABEL,
        links: createCitiesRows(menu),
      });

      bindCityRows({ citiesPanel, menu, addPanel, showPanel, hasPanel });
    }

    if (menu.currentCity) {
      addCityPanel({
        addPanel,
        city: menu.currentCity,
        parentName: menu.showAllCities ? LINK_NAME : null,
      });
    }

    const rootRow = element.querySelector(`[data-panel-target="${rootPanelName(menu)}"]`);
    rootRow?.addEventListener('click', () => trackInteraction('click'));
  };
}

/**
 * Puts the "Browse spaces" row at the top of the mobile avatar menu - on a phone or a tablet - on pages
 * that render the menu.
 */
export function withBrowseSpacesLink({ links, user }) {
  const root = findRoot();

  if (!root || !isAvailableFor(user) || !isAvatarMenuMobile()) {
    return links;
  }

  viewer = user;
  const menu = getMenu(root);

  return [
    {
      name: LINK_NAME,
      label: LABEL,
      url: '#',
      // Like any row that opens a sub-panel, it is marked while the page in view is one of its links.
      options: { icon: 'globe', panelTarget: rootPanelName(menu), current: isInMenu(menu) },
      createPanels: createPanels(menu),
    },
    ...links,
  ];
}
